import { Button, ButtonGroup, Dialog } from "@hightouch/htui";
import type { VideoExportTarget } from "src/pages/ad-studio/briefs/hooks/bulk-video-download-targets";
import { useBulkVideoDownload } from "src/pages/ad-studio/briefs/hooks/use-bulk-video-download";
import {
  DownloadProgressBody,
  type StaticDownloadProgress,
  type VideoDownloadJob,
} from "src/pages/ad-studio/components/download-progress";
import {
  type ExportSettingsArgs,
  type ExportSettingsResult,
  useExportSettingsState,
} from "src/pages/ad-studio/disclaimer/export-settings";
import { ExportSettingsBody } from "src/pages/ad-studio/disclaimer/export-settings-body";

// Stable so the video hook's start effect doesn't re-run while there is no job.
const NO_TARGETS: VideoExportTarget[] = [];

export type DownloadDialogSettings = ExportSettingsArgs & {
  /** The dialog only collects settings; the caller runs the download. */
  onConfirm: (result: ExportSettingsResult) => void;
};

export type DownloadDialogProgress = {
  staticProgress: StaticDownloadProgress;
  videoJob: VideoDownloadJob | null;
  /** Base name for the video archive, without an extension. */
  videoFilename: string;
};

export interface DownloadDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /** The settings step. Shown until `progress` takes over. */
  settings?: DownloadDialogSettings;
  /** The progress step. Set it after confirm and the body swaps in place. */
  progress?: DownloadDialogProgress;
}

/**
 * One modal for a download: collects export settings, then reports progress.
 * Both steps render through a single `Dialog` so the switch swaps the body in
 * place instead of cross-fading two modals. Owns the video pipeline for the
 * progress step, so closing abandons any renders still in flight.
 */
export const DownloadDialog = ({
  isOpen,
  onClose,
  settings,
  progress,
}: DownloadDialogProps) => {
  const settingsState = useExportSettingsState({
    staticAssets: settings?.staticAssets,
    videoAssets: settings?.videoAssets,
  });
  const video = useBulkVideoDownload({
    targets: progress?.videoJob?.selection.targets ?? NO_TARGETS,
    filename: progress?.videoFilename ?? "",
    disclaimerSpec: progress?.videoJob?.spec,
  });

  if (progress) {
    const isRunning =
      progress.staticProgress.phase === "building" ||
      progress.staticProgress.phase === "zipping" ||
      video.phase === "rendering" ||
      video.phase === "bundling";
    return (
      <Dialog
        isOpen={isOpen}
        variant="form"
        width="lg"
        maxHeight="85vh"
        title="Export"
        onClose={onClose}
        actions={
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              {isRunning ? "Cancel" : "Done"}
            </Button>
          </ButtonGroup>
        }
      >
        <DownloadProgressBody
          staticProgress={progress.staticProgress}
          videoJob={progress.videoJob}
          video={video}
        />
      </Dialog>
    );
  }

  return (
    <Dialog
      isOpen={isOpen}
      variant="form"
      // Wide enough to seat the preview beside the settings; narrower without one.
      width={settingsState.showStaticDisclosures ? "5xl" : "3xl"}
      // Content-sized; the body scrolls once the groups outgrow the viewport.
      maxHeight="85vh"
      title="Export settings"
      onClose={onClose}
      actions={
        <ButtonGroup>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => settings?.onConfirm(settingsState.buildResult())}
            isDisabled={!settingsState.canConfirm}
          >
            Download
          </Button>
        </ButtonGroup>
      }
    >
      <ExportSettingsBody
        staticAssets={settings?.staticAssets}
        videoAssets={settings?.videoAssets}
        state={settingsState}
      />
    </Dialog>
  );
};
